import {
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { Loader2Icon } from 'lucide-react';
import { unparse } from 'papaparse';
import { useMemo, useState } from 'react';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Alert, AlertDescription } from '~shared/shadcn/ui/alert';
import { Button } from '~shared/shadcn/ui/button';
import { Progress } from '~shared/shadcn/ui/progress';

import {
  collectRequiredFieldIssues,
  type ParentRegistrationData,
  validateCell,
} from './validation';

export interface ValidateCSVProps {
  csvData: Array<ParentRegistrationData>;
  embedded?: boolean;
  setStep?: React.Dispatch<
    React.SetStateAction<'select' | 'mapping' | 'validate'>
  >;
  columnHeaders?: Array<string>;
  handleUpload?: () => Promise<void>;
  isUploading?: boolean;
  /** 0–100 while posting CSV to queue the import */
  uploadProgress?: number | null;
}

const columnHelper = createColumnHelper<ParentRegistrationData>();

function getMaxStudentFromCsvData(
  csvData: Array<ParentRegistrationData>,
): number {
  const keys = Object.keys(csvData[0] ?? {});
  const re = /^Student (\d+) /;
  let max = 0;
  for (const k of keys) {
    const m = re.exec(k);
    if (m?.[1]) max = Math.max(max, parseInt(m[1], 10));
  }
  return max;
}

const generateStudentColumns = (studentNumber: number) => {
  const prefix = `Student ${studentNumber}`;
  return [
    columnHelper.accessor(
      `${prefix} First Name` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} First Name`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} First Name` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      `${prefix} Last Name` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Last Name`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Last Name` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      `${prefix} Date of Birth` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Date of Birth`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Date of Birth` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(`${prefix} Gender` as keyof ParentRegistrationData, {
      header: (info) => (
        <DataTableColumnHeader
          title={`${prefix} Gender`}
          column={info.column}
          className='pl-4 text-black'
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        const validation = validateCell(
          `${prefix} Gender` as keyof ParentRegistrationData,
          value,
        );
        return (
          <div
            className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
            title={validation.errorMessage}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor(`${prefix} Email` as keyof ParentRegistrationData, {
      header: (info) => (
        <DataTableColumnHeader
          title={`${prefix} Email`}
          column={info.column}
          className='pl-4 text-black'
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        const validation = validateCell(
          `${prefix} Email` as keyof ParentRegistrationData,
          value,
        );
        return (
          <div
            className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
            title={validation.errorMessage}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor(
      `${prefix} Primary Phone` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Primary Phone`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Primary Phone` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      `${prefix} Costume Size` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Costume Size`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Costume Size` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      `${prefix} Transportation` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Transportation`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Transportation` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      `${prefix} Health Insurance Carrier` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Health Insurance Carrier`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Health Insurance Carrier` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      `${prefix} Primary Doctor` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Primary Doctor`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Primary Doctor` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      `${prefix} Allergies` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Allergies`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Allergies` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      `${prefix} Disabilities` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Disabilities`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Disabilities` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      `${prefix} Medications` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Medications`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Medications` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      `${prefix} Special Needs` as keyof ParentRegistrationData,
      {
        header: (info) => (
          <DataTableColumnHeader
            title={`${prefix} Special Needs`}
            column={info.column}
            className='pl-4 text-black'
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          const validation = validateCell(
            `${prefix} Special Needs` as keyof ParentRegistrationData,
            value,
          );
          return (
            <div
              className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
              title={validation.errorMessage}
            >
              {value}
            </div>
          );
        },
      },
    ),
  ];
};

const baseColumns = [
  // Parent Information columns
  columnHelper.accessor('Parent First Name', {
    header: (info) => (
      <DataTableColumnHeader
        title='Parent First Name'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Parent First Name', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Parent Last Name', {
    header: (info) => (
      <DataTableColumnHeader
        title='Parent Last Name'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Parent Last Name', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Parent Email', {
    header: (info) => (
      <DataTableColumnHeader
        title='Parent Email'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Parent Email', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Parent Primary Phone', {
    header: (info) => (
      <DataTableColumnHeader
        title='Parent Primary Phone'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Parent Primary Phone', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Relation', {
    header: (info) => (
      <DataTableColumnHeader
        title='Relation'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Relation', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Address Apartment', {
    header: (info) => (
      <DataTableColumnHeader
        title='Address_Apartment'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Address Apartment', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Address City', {
    header: (info) => (
      <DataTableColumnHeader
        title='Address City'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Address City', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Address Country', {
    header: (info) => (
      <DataTableColumnHeader
        title='Address Country'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Address Country', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Address State', {
    header: (info) => (
      <DataTableColumnHeader
        title='Address State'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Address State', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Address Street', {
    header: (info) => (
      <DataTableColumnHeader
        title='Address Street'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Address Street', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Address Zip', {
    header: (info) => (
      <DataTableColumnHeader
        title='Address Zip'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Address Zip', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Emergency Contact 1 Authorize Pickup', {
    header: (info) => (
      <DataTableColumnHeader
        title='Emergency Contact 1 Authorize Pickup'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell(
        'Emergency Contact 1 Authorize Pickup',
        value,
      );
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Emergency Contact 1 Email', {
    header: (info) => (
      <DataTableColumnHeader
        title='Emergency Contact 1 Email'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Emergency Contact 1 Email', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Emergency Contact 1 First Name', {
    header: (info) => (
      <DataTableColumnHeader
        title='Emergency Contact 1 First Name'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Emergency Contact 1 First Name', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Emergency Contact 1 Last Name', {
    header: (info) => (
      <DataTableColumnHeader
        title='Emergency Contact 1 Last Name'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell('Emergency Contact 1 Last Name', value);
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
  columnHelper.accessor('Emergency Contact 1 Primary Phone', {
    header: (info) => (
      <DataTableColumnHeader
        title='Emergency Contact 1 Primary Phone'
        column={info.column}
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      const validation = validateCell(
        'Emergency Contact 1 Primary Phone',
        value,
      );
      return (
        <div
          className={`p-2 ${!validation.isValid ? 'bg-red-200' : ''}`}
          title={validation.errorMessage}
        >
          {value}
        </div>
      );
    },
  }),
];

export const ValidateCSV = ({
  csvData,
  embedded = false,
  setStep,
  handleUpload,
  isUploading = false,
  uploadProgress,
}: ValidateCSVProps) => {
  const maxStudent = useMemo(
    () => getMaxStudentFromCsvData(csvData),
    [csvData],
  );

  const columns = useMemo(
    () => [
      ...baseColumns,
      ...Array.from({ length: maxStudent }, (_, i) =>
        generateStudentColumns(i + 1),
      ).flat(),
    ],
    [maxStudent],
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });
  const [showOnlyInvalid, setShowOnlyInvalid] = useState(false);

  const validationIssues = useMemo(
    () => collectRequiredFieldIssues(csvData),
    [csvData],
  );
  const invalidRowNumbers = useMemo(
    () => new Set(validationIssues.map((issue) => issue.rowNumber)),
    [validationIssues],
  );
  const isCSVDataValid = csvData.every((row) =>
    (Object.keys(row) as Array<keyof ParentRegistrationData>).every(
      (key) => validateCell(key, row[key]).isValid,
    ),
  );
  const issuesByColumn = useMemo(() => {
    const grouped = new Map<string, { rows: Array<number>; message: string }>();
    for (const issue of validationIssues) {
      const existing = grouped.get(issue.column);
      if (existing) {
        existing.rows.push(issue.rowNumber);
      } else {
        grouped.set(issue.column, {
          rows: [issue.rowNumber],
          message: issue.errorMessage,
        });
      }
    }
    return Array.from(grouped.entries());
  }, [validationIssues]);

  const displayData = useMemo(
    () =>
      showOnlyInvalid
        ? csvData.filter((_, index) => invalidRowNumbers.has(index + 1))
        : csvData,
    [csvData, showOnlyInvalid, invalidRowNumbers],
  );

  const toggleShowOnlyInvalid = () => {
    setShowOnlyInvalid((prev) => !prev);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleDownloadCSV = () => {
    const csvDataNew = csvData.map((row) => {
      const errors: Array<string> = [];
      const newRow: Record<string, string | number | undefined> = { ...row };
      Object.keys(row).forEach((key) => {
        const validation = validateCell(
          key as keyof ParentRegistrationData,
          row[key as keyof ParentRegistrationData],
        );
        if (!validation.isValid) {
          newRow[key as keyof ParentRegistrationData] =
            `[INVALID: ${row[key as keyof ParentRegistrationData]}]`;
          errors.push(`${key}: ${validation.errorMessage}`);
        }
      });
      newRow['Validation Errors'] = errors.join('; ');
      return newRow;
    });

    const csv = unparse(csvDataNew);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'class_data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {validationIssues.length > 0 && (
        <Alert variant='destructive' className='mb-4'>
          <AlertDescription>
            <div className='font-medium'>
              {validationIssues.length} issue
              {validationIssues.length === 1 ? '' : 's'} across{' '}
              {invalidRowNumbers.size} row
              {invalidRowNumbers.size === 1 ? '' : 's'} must be fixed before you
              can upload.
            </div>
            <ul className='mt-2 list-disc space-y-1 pl-5 text-sm'>
              {issuesByColumn.map(([column, { rows, message }]) => (
                <li key={column}>
                  <span className='font-medium'>{column}</span>: {message} (
                  {rows.length === 1 ? 'row' : 'rows'}{' '}
                  {rows.slice(0, 10).join(', ')}
                  {rows.length > 10 ? ` +${rows.length - 10} more` : ''})
                </li>
              ))}
            </ul>
            <Button
              variant='outline'
              size='sm'
              className='mt-3'
              onClick={toggleShowOnlyInvalid}
            >
              {showOnlyInvalid ? 'Show all rows' : 'Show only invalid rows'}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <EnrollioTable
        ariaLabel='Parent CSV validation results'
        totalLabel='CSV rows'
        filtersLayout='inline'
        filterHeader={(table) => {
          const parentNameColumn = table.getColumn('Parent First Name');
          return (
            <EnrollioTableSearch
              value={
                (parentNameColumn?.getFilterValue() as string | undefined) ?? ''
              }
              onChange={(value) => {
                parentNameColumn?.setFilterValue(value);
              }}
              placeholder='Search parent first name...'
            />
          );
        }}
        toolbar={(table) => (
          <>
            {embedded && isCSVDataValid ? null : (
              <Button variant={'secondary'} onClick={handleDownloadCSV}>
                Download CSV
              </Button>
            )}
            <EnrollioTableColumnToggle table={table} />
          </>
        )}
        data={displayData}
        columns={columns}
        pagination={pagination}
        onPaginationChange={setPagination}
      />
      {isUploading && (
        <div className='mt-4 space-y-2'>
          <div className='flex justify-between text-sm text-muted-foreground'>
            <span>Uploading file…</span>
            <span>{uploadProgress != null ? `${uploadProgress}%` : ''}</span>
          </div>
          <Progress value={uploadProgress ?? 0} className='h-2' />
        </div>
      )}
      {!embedded && setStep && handleUpload && (
        <div className='mt-4 flex items-center justify-start gap-2'>
          <Button
            variant={'outline'}
            onClick={() => {
              setStep('mapping');
            }}
          >
            Back
          </Button>
          <Button
            disabled={!isCSVDataValid || isUploading}
            onClick={handleUpload}
          >
            {isUploading ? <Loader2Icon className='animate-spin' /> : 'Upload'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ValidateCSV;
